/**
 * Iter-284e — Gallery Approvals admin page.
 *
 * Admins review imported (TrustedHerd / StaffConnect) and native gallery
 * photos and decide visibility (admin_only / talent_visible / client_visible
 * / public_profile). Every change is audited via
 * /api/admin/gallery-approvals/bulk → talent_media_visibility_audit.
 *
 * Has a Preview Mode that re-renders exactly what a chosen viewer role
 * would see for a single talent — to derisk approval decisions.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Eye, ShieldCheck, Globe, Lock, Users as UsersIcon, Search, RotateCcw, Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../utils/api';
import { Link } from 'react-router-dom';

const VISIBILITY_META = {
  admin_only:     { label: 'Admin only',     color: 'bg-rose-50 text-rose-700 border-rose-200',     icon: Lock,        chip: 'rose' },
  talent_visible: { label: 'Talent only',    color: 'bg-amber-50 text-amber-700 border-amber-200',  icon: UsersIcon,   chip: 'amber' },
  client_visible: { label: 'Client visible', color: 'bg-sky-50 text-sky-700 border-sky-200',        icon: ShieldCheck, chip: 'sky' },
  public_profile: { label: 'Public',         color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Globe, chip: 'emerald' },
};

const SOURCE_LABEL = {
  trustedherd_import: 'TrustedHerd',
  staffconnect_import: 'StaffConnect',
  native_upload: 'Native',
};

export default function GalleryApprovals() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(40);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filters
  const [source, setSource] = useState('all');
  const [visibility, setVisibility] = useState('');
  const [talentQ, setTalentQ] = useState('');
  const [workerId, setWorkerId] = useState('');
  const [hasProfilePhoto, setHasProfilePhoto] = useState('');
  const [hasGalleryPhoto, setHasGalleryPhoto] = useState('');

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Preview modal state
  const [previewTalentId, setPreviewTalentId] = useState(null);
  const [previewViewer, setPreviewViewer] = useState('admin');
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Audit modal state
  const [auditMediaId, setAuditMediaId] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  const fetchSummary = useCallback(async () => {
    try {
      const r = await api.get('/admin/gallery-approvals/summary');
      setSummary(r.data);
    } catch (e) {
      console.error('summary fetch', e);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (source && source !== 'all') params.set('source', source);
      if (visibility) params.set('visibility', visibility);
      if (talentQ.trim()) params.set('talent_query', talentQ.trim());
      if (workerId.trim()) params.set('worker_id', workerId.trim());
      if (hasProfilePhoto !== '') params.set('has_profile_photo', hasProfilePhoto);
      if (hasGalleryPhoto !== '') params.set('has_gallery_photo', hasGalleryPhoto);
      params.set('page', String(page));
      params.set('per_page', String(perPage));
      const r = await api.get(`/admin/gallery-approvals?${params.toString()}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
    } catch (e) {
      toast.error('Failed to load gallery photos: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [source, visibility, talentQ, workerId, hasProfilePhoto, hasGalleryPhoto, page, perPage]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchList(); }, [fetchList]);

  // Debounce free-text filters
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchList(); }, 400);
    return () => clearTimeout(t);
  }, [talentQ, workerId, fetchList]);

  const toggleSelected = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };

  const applyBulk = async (newVisibility) => {
    if (selected.size === 0) {
      toast.warning('Select at least one photo first');
      return;
    }
    const reason = window.prompt(
      `Set ${selected.size} photo${selected.size === 1 ? '' : 's'} to "${VISIBILITY_META[newVisibility].label}".\n\nOptional reason (audited):`,
      ''
    );
    if (reason === null) return;  // cancel
    try {
      const r = await api.post('/admin/gallery-approvals/bulk', {
        media_ids: Array.from(selected),
        new_visibility: newVisibility,
        reason: reason || null,
      });
      toast.success(`Updated ${r.data.updated} photo(s); audit rows: ${r.data.audit_rows}`);
      setSelected(new Set());
      await fetchSummary();
      await fetchList();
    } catch (e) {
      toast.error('Bulk update failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const applySingle = async (mediaId, newVisibility) => {
    try {
      await api.post('/admin/gallery-approvals/bulk', {
        media_ids: [mediaId],
        new_visibility: newVisibility,
      });
      toast.success(`Set to ${VISIBILITY_META[newVisibility].label}`);
      await fetchSummary();
      await fetchList();
    } catch (e) {
      toast.error('Update failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const openPreview = async (talentUserId, viewer = 'admin') => {
    setPreviewTalentId(talentUserId);
    setPreviewViewer(viewer);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const r = await api.get(`/admin/gallery-approvals/preview/${talentUserId}?viewer=${viewer}`);
      setPreviewData(r.data);
    } catch (e) {
      toast.error('Preview failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const switchPreviewViewer = async (viewer) => {
    if (!previewTalentId) return;
    await openPreview(previewTalentId, viewer);
  };

  const openAudit = async (mediaId) => {
    setAuditMediaId(mediaId);
    setAuditRows([]);
    try {
      const r = await api.get(`/admin/gallery-approvals/${mediaId}/audit`);
      setAuditRows(r.data.rows || []);
    } catch (e) {
      toast.error('Audit fetch failed');
    }
  };

  const resetFilters = () => {
    setSource('all'); setVisibility(''); setTalentQ(''); setWorkerId('');
    setHasProfilePhoto(''); setHasGalleryPhoto(''); setPage(1);
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-testid="gallery-approvals-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gallery Approvals</h1>
          <p className="text-sm text-gray-500 mt-1">Promote imported StaffConnect &amp; TrustedHerd gallery photos to client &amp; public visibility.</p>
        </div>
        <Link to="/admin/users" className="text-sm text-cyan-700 hover:underline">← Back to Users</Link>
      </div>

      {/* Stat strip */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <Card className="border-rose-200 bg-rose-50/40" data-testid="stat-visible-admin">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-rose-700">{summary.visible_to_admin.toLocaleString()}</div>
              <div className="text-xs text-rose-600">Visible to admin</div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/40" data-testid="stat-visible-talent">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-amber-700">{summary.visible_to_talent_owner.toLocaleString()}</div>
              <div className="text-xs text-amber-600">Visible to talent owner</div>
            </CardContent>
          </Card>
          <Card className="border-sky-200 bg-sky-50/40" data-testid="stat-visible-client">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-sky-700">{summary.visible_to_client.toLocaleString()}</div>
              <div className="text-xs text-sky-600">Visible to client</div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/40" data-testid="stat-visible-public">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-emerald-700">{summary.visible_to_public.toLocaleString()}</div>
              <div className="text-xs text-emerald-600">Visible to public</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Source</label>
            <Select value={source} onValueChange={v => { setSource(v); setPage(1); }}>
              <SelectTrigger data-testid="filter-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="trustedherd_import">TrustedHerd</SelectItem>
                <SelectItem value="staffconnect_import">StaffConnect</SelectItem>
                <SelectItem value="native_upload">Native</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Visibility</label>
            <Select value={visibility || 'any'} onValueChange={v => { setVisibility(v === 'any' ? '' : v); setPage(1); }}>
              <SelectTrigger data-testid="filter-visibility"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="admin_only">Admin only</SelectItem>
                <SelectItem value="talent_visible">Talent only</SelectItem>
                <SelectItem value="client_visible">Client visible</SelectItem>
                <SelectItem value="public_profile">Public</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-gray-500 block mb-1">Talent name / email</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <Input className="pl-8" value={talentQ} onChange={e => setTalentQ(e.target.value)} placeholder="e.g. Caitlin or @gmail.com" data-testid="filter-talent-query" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Worker ID</label>
            <Input value={workerId} onChange={e => setWorkerId(e.target.value)} placeholder="exact id" data-testid="filter-worker-id" />
          </div>
          <div className="flex items-end gap-2">
            <Button variant="outline" onClick={resetFilters} data-testid="reset-filters-btn"><RotateCcw className="w-4 h-4 mr-1" /> Reset</Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      <Card className="mb-4">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <Badge variant="outline" data-testid="selected-count">{selected.size} selected</Badge>
          <Button size="sm" variant="outline" onClick={toggleSelectAll} data-testid="toggle-select-all">
            {selected.size === rows.length && rows.length > 0 ? 'Deselect page' : 'Select page'}
          </Button>
          <div className="flex-1" />
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => applyBulk('talent_visible')} data-testid="bulk-talent-visible">
            <UsersIcon className="w-4 h-4 mr-1" /> Keep Talent Only
          </Button>
          <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" onClick={() => applyBulk('client_visible')} data-testid="bulk-client-visible">
            <ShieldCheck className="w-4 h-4 mr-1" /> Approve for Client
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => applyBulk('public_profile')} data-testid="bulk-public-profile">
            <Globe className="w-4 h-4 mr-1" /> Approve for Public
          </Button>
          <Button size="sm" variant="destructive" onClick={() => applyBulk('admin_only')} data-testid="bulk-admin-only">
            <Lock className="w-4 h-4 mr-1" /> Hide
          </Button>
        </CardContent>
      </Card>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 mb-6" data-testid="approval-grid">
        {loading && rows.length === 0 && (
          Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="aspect-square bg-gray-100 animate-pulse rounded" />
          ))
        )}
        {!loading && rows.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-40" />
            No gallery photos match the current filters.
          </div>
        )}
        {rows.map(r => {
          const meta = VISIBILITY_META[r.visibility] || VISIBILITY_META.admin_only;
          const isSelected = selected.has(r.id);
          return (
            <div
              key={r.id}
              className={`relative rounded-lg overflow-hidden border-2 ${isSelected ? 'border-cyan-500' : 'border-transparent'} bg-gray-100`}
              data-testid={`gallery-card-${r.id}`}
            >
              <input
                type="checkbox"
                className="absolute top-2 left-2 z-10 w-4 h-4 cursor-pointer accent-cyan-600"
                checked={isSelected}
                onChange={() => toggleSelected(r.id)}
                data-testid={`select-${r.id}`}
              />
              <div className="aspect-square">
                <img
                  src={r.image_url}
                  alt={r.original_name || ''}
                  className="w-full h-full object-cover cursor-pointer"
                  loading="lazy"
                  decoding="async"
                  onClick={() => openPreview(r.talent_user_id, 'admin')}
                  onError={(e) => { e.currentTarget.style.opacity = '0.2'; }}
                  data-testid={`gallery-img-${r.id}`}
                />
              </div>
              <span className={`absolute top-2 right-2 text-[10px] font-medium px-1.5 py-0.5 rounded border ${meta.color}`} data-testid={`visibility-badge-${r.id}`}>
                {meta.label}
              </span>
              <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-gradient-to-t from-black/70 to-transparent text-white text-xs">
                <div className="truncate font-medium" data-testid={`talent-name-${r.id}`}>{r.talent.full_name || r.talent.email}</div>
                <div className="text-[10px] opacity-80 flex items-center justify-between">
                  <span>{SOURCE_LABEL[r.source] || r.source}{r.talent.external_worker_id ? ` · #${r.talent.external_worker_id}` : ''}</span>
                </div>
              </div>
              <div className="absolute inset-0 opacity-0 hover:opacity-100 transition bg-black/40 flex items-end justify-center pb-12 gap-1 pointer-events-none">
                <Button size="sm" variant="secondary" className="pointer-events-auto h-7 text-xs" onClick={() => openPreview(r.talent_user_id, 'admin')} data-testid={`preview-${r.id}`}>
                  <Eye className="w-3 h-3 mr-1" /> Preview
                </Button>
                <Button size="sm" variant="secondary" className="pointer-events-auto h-7 text-xs" onClick={() => openAudit(r.id)} data-testid={`audit-${r.id}`}>
                  History
                </Button>
              </div>
              <div className="flex gap-1 p-1 bg-white border-t border-gray-200">
                <button className="flex-1 text-[10px] py-1 rounded bg-amber-50 hover:bg-amber-100 text-amber-700" onClick={() => applySingle(r.id, 'talent_visible')} data-testid={`set-talent-visible-${r.id}`}>Talent</button>
                <button className="flex-1 text-[10px] py-1 rounded bg-sky-50 hover:bg-sky-100 text-sky-700" onClick={() => applySingle(r.id, 'client_visible')} data-testid={`set-client-visible-${r.id}`}>Client</button>
                <button className="flex-1 text-[10px] py-1 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700" onClick={() => applySingle(r.id, 'public_profile')} data-testid={`set-public-${r.id}`}>Public</button>
                <button className="flex-1 text-[10px] py-1 rounded bg-rose-50 hover:bg-rose-100 text-rose-700" onClick={() => applySingle(r.id, 'admin_only')} data-testid={`set-admin-only-${r.id}`}>Hide</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500" data-testid="results-count">
          {total.toLocaleString()} photo{total === 1 ? '' : 's'} · page {page} of {totalPages.toLocaleString()}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Preview modal */}
      <Dialog open={!!previewTalentId} onOpenChange={(open) => { if (!open) { setPreviewTalentId(null); setPreviewData(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Preview Mode</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="preview-viewer-toggle">
            {['admin', 'talent', 'client', 'public'].map(v => (
              <Button
                key={v}
                size="sm"
                variant={previewViewer === v ? 'default' : 'outline'}
                onClick={() => switchPreviewViewer(v)}
                data-testid={`preview-as-${v}`}
              >
                {v === 'admin' && <ShieldCheck className="w-4 h-4 mr-1" />}
                {v === 'talent' && <UsersIcon className="w-4 h-4 mr-1" />}
                {v === 'client' && <Eye className="w-4 h-4 mr-1" />}
                {v === 'public' && <Globe className="w-4 h-4 mr-1" />}
                As {v.charAt(0).toUpperCase() + v.slice(1)}
              </Button>
            ))}
          </div>
          {previewLoading && <div className="py-10 text-center text-gray-500">Loading…</div>}
          {previewData && (
            <div>
              <div className="flex items-center gap-3 mb-4 pb-3 border-b">
                <div className="w-16 h-16 rounded-full bg-gray-200 overflow-hidden">
                  {previewData.talent.profile_photo_url && (
                    <img src={previewData.talent.profile_photo_url} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div>
                  <div className="font-semibold text-lg">{previewData.talent.first_name} {previewData.talent.last_name}</div>
                  <div className="text-xs text-gray-500" data-testid="preview-contact">
                    {previewData.talent.email || <em className="text-gray-400">email hidden</em>}
                    {previewData.talent.phone && ` · ${previewData.talent.phone}`}
                  </div>
                  <div className="text-xs text-gray-400">
                    Gallery items visible to <strong>{previewData.viewer}</strong>: <span data-testid="preview-gallery-count">{previewData.gallery_count}</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[60vh] overflow-y-auto">
                {previewData.gallery.map(g => (
                  <div key={g.id} className="aspect-square rounded overflow-hidden bg-gray-100 relative">
                    <img src={g.image_url} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    <span className={`absolute top-1 left-1 text-[9px] px-1 rounded ${VISIBILITY_META[g.visibility]?.color || ''}`}>
                      {VISIBILITY_META[g.visibility]?.label}
                    </span>
                  </div>
                ))}
                {previewData.gallery_count === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-400 text-sm" data-testid="preview-empty">
                    Nothing visible to {previewData.viewer}.
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Audit modal */}
      <Dialog open={!!auditMediaId} onOpenChange={(open) => { if (!open) setAuditMediaId(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Visibility History</DialogTitle></DialogHeader>
          {auditRows.length === 0 && (
            <div className="text-sm text-gray-500 py-4">No changes recorded yet.</div>
          )}
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {auditRows.map(a => (
              <div key={a.id} className="border rounded p-3 text-sm" data-testid={`audit-row-${a.id}`}>
                <div className="flex items-center justify-between">
                  <Badge variant="outline">{a.old_visibility || '(initial)'} → {a.new_visibility}</Badge>
                  <span className="text-xs text-gray-500">{new Date(a.created_at).toLocaleString()}</span>
                </div>
                <div className="text-xs text-gray-600 mt-1">by {a.actor_display || a.actor_email}</div>
                {a.reason && <div className="text-xs italic text-gray-500 mt-1">&ldquo;{a.reason}&rdquo;</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
